import React, { useEffect, useState } from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { WalletTransaction, PaginatedTransactions } from '../types/wallet.types'
import { formatINR, formatIST } from '../../../shared/utils/formatters'
import { walletsService } from '../../../shared/api/services/wallets.service'

const { Text } = Typography

interface WalletTransactionsTableProps {
    merchantId: string
    refreshKey: number
}

export const WalletTransactionsTable: React.FC<WalletTransactionsTableProps> = ({ merchantId, refreshKey }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState<PaginatedTransactions | null>(null)
    const [page, setPage] = useState(1)

    const fetchTransactions = async (currentPage: number) => {
        try {
            setIsLoading(true)
            const result = await walletsService.getTransactions(merchantId, currentPage)
            setData(result)
            setPage(currentPage)
        } catch (error) {
            console.error('Failed to fetch transactions', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchTransactions(1)
    }, [merchantId, refreshKey])

    const columns: ColumnsType<WalletTransaction> = [
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date: string) => formatIST(date)
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type?: string) => (
                <Tag color={type === 'credit' ? 'green' : 'red'}>
                    {(type || 'unknown').toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Reference',
            dataIndex: 'reference_type',
            key: 'reference_type',
            render: (type?: string) => (
                <Tag color="blue">{(type || 'manual').toUpperCase()}</Tag>
            )
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number | string, record: WalletTransaction) => (
                <Text type={record.type === 'debit' ? 'danger' : 'success'}>
                    {record.type === 'debit' ? '-' : '+'} {formatINR(amount)}
                </Text>
            )
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description'
        },
        {
            title: 'Balance After',
            dataIndex: 'balance_after',
            key: 'balance_after',
            render: (balance: number | string) => formatINR(balance)
        }
    ]

    return (
        <Table
            columns={columns}
            dataSource={data?.data || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
                current: page,
                pageSize: data?.per_page || 15,
                total: data?.total || 0,
                onChange: (p) => fetchTransactions(p),
                showSizeChanger: false
            }}
        />
    )
}
